import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  Timestamp,
  query,
  writeBatch,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { COVER_IMAGE_FORMAT, SOCIAL_IMAGE_FORMAT, compressImageFile } from './imageCompression';
import { db, storage } from './firebase';
import { DEFAULT_AUTHOR, DEFAULT_AUTHOR_ID } from './authors';
import { buildArticleSlugRegistryId, createEmptyArticle, createEmptyCategory, createEmptyLocalizedField, normalizeLocalizedSlug, normalizeTranslationStatus, slugify } from './blogSchema';

export const ARTICLES_COLLECTION = 'articles';
export const CATEGORIES_COLLECTION = 'categories';
export const ARTICLE_SLUGS_COLLECTION = 'articleSlugs';
export const DEFAULT_ARTICLE_PAGE_SIZE = 12;

const getImageDimensions = (file) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    resolve({ width: image.naturalWidth || 0, height: image.naturalHeight || 0 });
    URL.revokeObjectURL(objectUrl);
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Unable to read image dimensions.'));
  };

  image.src = objectUrl;
});

const getFileExtension = (file) => {
  const rawName = String(file?.name || '');
  const parts = rawName.split('.');
  const directExt = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';

  if (directExt) return directExt;

  const mimeType = String(file?.type || '').toLowerCase();
  if (mimeType.includes('png')) return 'png';
  if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'jpg';
  if (mimeType.includes('gif')) return 'gif';
  if (mimeType.includes('webp')) return 'webp';
  return 'bin';
};

export const generateArticleId = () => doc(getCollectionRef(ARTICLES_COLLECTION)).id;

export const uploadArticleImageAsset = async ({ articleId, file: originalFile, variant = 'cover' } = {}) => {
  if (!storage) {
    throw new Error('Firebase Storage is not configured.');
  }

  if (!articleId) {
    throw new Error('articleId is required for media uploads.');
  }

  if (!(originalFile instanceof File)) {
    throw new Error('A valid image file is required.');
  }

  const file = await compressImageFile(originalFile, variant === 'social' ? SOCIAL_IMAGE_FORMAT : COVER_IMAGE_FORMAT);
  const ext = getFileExtension(file);
  const baseName = slugify(file.name.replace(/\.[^/.]+$/, '')) || 'image';
  const stampedName = `${Date.now()}-${baseName}.${ext}`;
  const path = `blog/articles/${articleId}/${variant}/${stampedName}`;

  try {
    const [dimensions, uploadResult] = await Promise.all([
      getImageDimensions(file),
      uploadBytes(storageRef(storage, path), file, {
        contentType: file.type || 'application/octet-stream',
        cacheControl: 'public, max-age=31536000, immutable',
      }),
    ]);

    const url = await getDownloadURL(uploadResult.ref);

    return {
      storagePath: uploadResult.ref.fullPath,
      url,
      width: dimensions.width,
      height: dimensions.height,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: Number.isFinite(file.size) ? file.size : 0,
    };
  } catch (error) {
    if (error?.code === 'storage/unauthorized') {
      throw new Error('Storage denied this upload. Check Storage rules and admin auth state.');
    }

    throw error;
  }
};

// Photos Fer adds in the editor for Claude to place ("Ask Claude for changes",
// functions/news/revise.js). Phone photos and game screenshots weigh several
// MB, so they're shrunk in the browser to at most 1600 px wide and re-encoded
// as WebP (browsers that can't encode WebP from a canvas hand back PNG).
const INLINE_IMAGE_MAX_WIDTH = 1600;
export const MAX_INBOX_IMAGES = 12;

const shrinkImage = (file) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    const scale = Math.min(1, INLINE_IMAGE_MAX_WIDTH / (image.naturalWidth || 1));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => (blob
      ? resolve({ blob, width: canvas.width, height: canvas.height })
      : reject(new Error('Unable to compress the image.'))), 'image/webp', 0.85);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error(`Unable to read ${file.name || 'the image'}.`));
  };
  image.src = objectUrl;
});

export const uploadArticleInlineImage = async ({ articleId, file }) => {
  if (!storage) throw new Error('Firebase Storage is not configured.');
  if (!articleId) throw new Error('Save the article before adding photos.');
  const { blob, width, height } = await shrinkImage(file);
  const ext = blob.type === 'image/webp' ? 'webp' : 'png';
  const baseName = slugify(String(file.name || '').replace(/\.[^/.]+$/, '')).slice(0, 60) || 'photo';
  const path = `blog/articles/${articleId}/inline/${Date.now()}-${baseName}.${ext}`;
  try {
    const result = await uploadBytes(storageRef(storage, path), blob, {
      contentType: blob.type,
      cacheControl: 'public, max-age=31536000, immutable',
    });
    return { url: await getDownloadURL(result.ref), storagePath: result.ref.fullPath, width, height };
  } catch (error) {
    if (error?.code === 'storage/unauthorized') {
      throw new Error('Storage denied this upload. Check that storage.rules (inline photos) is deployed and that your admin token is fresh.');
    }
    throw error;
  }
};

export const normalizeInboxImages = (images) => (Array.isArray(images) ? images : [])
  .filter((item) => item && typeof item.url === 'string' && item.url.startsWith('https://'))
  .slice(0, MAX_INBOX_IMAGES)
  .map((item) => ({
    url: item.url,
    storagePath: typeof item.storagePath === 'string' ? item.storagePath : '',
    width: Number.isInteger(item.width) ? item.width : 0,
    height: Number.isInteger(item.height) ? item.height : 0,
  }));

// Saved on their own, right away, so a photo isn't lost if Fer leaves the
// editor without pressing Save.
export const setArticleInboxImages = async (articleId, images) => {
  if (!db) throw new Error('Firestore is not configured.');
  if (!articleId) throw new Error('Save the article before adding photos.');
  const inboxImages = normalizeInboxImages(images);
  try {
    await updateDoc(doc(db, ARTICLES_COLLECTION, articleId), { inboxImages, updatedAt: Timestamp.now() });
    return inboxImages;
  } catch (error) {
    if (error?.code === 'permission-denied') {
      throw new Error('Firestore denied saving the photos. Check the deployed articles rule and refresh the admin session token.');
    }
    throw error;
  }
};

const mapDocument = (snapshot) => ({ id: snapshot.id, ...snapshot.data() });

const mapSnapshotList = (snapshot) => snapshot.docs.map(mapDocument);

const isTimestampLike = (value) => value instanceof Timestamp || Boolean(value && typeof value.seconds === 'number' && typeof value.nanoseconds === 'number');

const validateLocalizedMap = (value, fieldName, issues) => {
  if (!value || typeof value !== 'object') {
    issues.push(`${fieldName} must be an object with es, en, and pt strings.`);
    return;
  }

  ['es', 'en', 'pt'].forEach((locale) => {
    if (typeof value[locale] !== 'string') {
      issues.push(`${fieldName}.${locale} must be a string.`);
    }
  });
};

const validateCategoryPayload = (payload) => {
  const issues = [];

  if (!payload?.id || typeof payload.id !== 'string') {
    issues.push('id is required.');
  }

  ['name', 'slug', 'description', 'seoTitle', 'seoDescription'].forEach((fieldName) => {
    validateLocalizedMap(payload?.[fieldName], fieldName, issues);
  });

  if (!Number.isInteger(payload?.sortOrder) || payload.sortOrder < 0 || payload.sortOrder > 10000) {
    issues.push('sortOrder must be an integer between 0 and 10000.');
  }

  if (typeof payload?.isActive !== 'boolean') {
    issues.push('isActive must be a boolean.');
  }

  if (!isTimestampLike(payload?.createdAt)) {
    issues.push('createdAt must be a Firestore timestamp.');
  }

  if (!isTimestampLike(payload?.updatedAt)) {
    issues.push('updatedAt must be a Firestore timestamp.');
  }

  if (issues.length > 0) {
    throw new Error(`Category payload validation failed: ${issues.join(' ')}`);
  }
};

const sanitizeCategoryPayload = (category) => {
  const normalized = createEmptyCategory(category);
  const now = Timestamp.now();

  return {
    ...normalized,
    id: slugify(normalized.id || normalized.slug?.en || normalized.slug?.es || normalized.name?.en || normalized.name?.es || ''),
    updatedAt: now,
    createdAt: normalized.createdAt || now,
    slug: normalizeLocalizedSlug(normalized.slug),
  };
};

const mapRegistryArticle = (snapshot) => {
  const data = snapshot.data();
  const localizedSlug = createEmptyLocalizedField();

  if (data.locale && data.slug) {
    localizedSlug[data.locale] = data.slug;
  }

  return {
    id: data.articleId || snapshot.id,
    articleId: data.articleId || '',
    status: data.status || 'draft',
    updatedAt: data.updatedAt || null,
    updatedBy: '',
    createdAt: null,
    createdBy: '',
    authorId: '',
    authorName: data.articleId ? 'Article registry' : 'Registry entry',
    localePriority: data.locale || 'en',
    title: createEmptyLocalizedField(),
    slug: localizedSlug,
    excerpt: createEmptyLocalizedField(),
    content: createEmptyLocalizedField(),
    seoTitle: createEmptyLocalizedField(),
    seoDescription: createEmptyLocalizedField(),
    coverImage: null,
    socialImage: null,
    categoryIds: [],
    tagIds: [],
    relatedArticleIds: [],
    featured: false,
    translationStatus: null,
    publishedAt: null,
    scheduledFor: null,
    __registryOnly: true,
  };
};

const buildSlugRegistryEntries = (article) => Object.entries(normalizeLocalizedSlug(article.slug || {}))
  .filter(([, slug]) => Boolean(slug))
  .map(([locale, slug]) => ({
    locale,
    slug,
    registryId: buildArticleSlugRegistryId(locale, slug),
  }));

const validateArticlePayload = (payload) => {
  const issues = [];

  if (!payload?.id || typeof payload.id !== 'string') {
    issues.push('id is required.');
  }

  if (!payload?.status || !['draft', 'published', 'scheduled', 'archived'].includes(payload.status)) {
    issues.push('status must be draft, published, scheduled, or archived.');
  }

  if (typeof payload?.authorId !== 'string' || !payload.authorId.trim()) {
    issues.push('authorId is required.');
  }

  if (typeof payload?.authorName !== 'string' || !payload.authorName.trim()) {
    issues.push('authorName is required.');
  }

  if (!['es', 'en', 'pt'].includes(payload?.localePriority)) {
    issues.push('localePriority must be es, en, or pt.');
  }

  ['title', 'slug', 'excerpt', 'content', 'seoTitle', 'seoDescription'].forEach((fieldName) => {
    validateLocalizedMap(payload?.[fieldName], fieldName, issues);
  });

  if (!payload?.coverImage || typeof payload.coverImage !== 'object') {
    issues.push('coverImage must be an object.');
  }

  if (!payload?.socialImage || typeof payload.socialImage !== 'object') {
    issues.push('socialImage must be an object.');
  }

  if (!Array.isArray(payload?.categoryIds)) {
    issues.push('categoryIds must be a list.');
  }

  if (!Array.isArray(payload?.tagIds)) {
    issues.push('tagIds must be a list.');
  }

  if (!Array.isArray(payload?.relatedArticleIds)) {
    issues.push('relatedArticleIds must be a list.');
  }

  if (!Number.isInteger(payload?.readingTimeMinutes) || payload.readingTimeMinutes < 0 || payload.readingTimeMinutes > 1440) {
    issues.push('readingTimeMinutes must be an integer between 0 and 1440.');
  }

  if (typeof payload?.featured !== 'boolean') {
    issues.push('featured must be a boolean.');
  }

  if (!payload?.translationStatus || typeof payload.translationStatus !== 'object') {
    issues.push('translationStatus must be an object.');
  }

  if (!isTimestampLike(payload?.createdAt)) {
    issues.push('createdAt must be a Firestore timestamp.');
  }

  if (!isTimestampLike(payload?.updatedAt)) {
    issues.push('updatedAt must be a Firestore timestamp.');
  }

  if (issues.length > 0) {
    throw new Error(`Article payload validation failed: ${issues.join(' ')}`);
  }
};

const sanitizeArticlePayload = (article, currentUser) => {
  const normalized = createEmptyArticle(article);
  const now = Timestamp.now();

  return {
    ...normalized,
    updatedAt: now,
    updatedBy: currentUser?.uid || normalized.updatedBy || '',
    authorId: normalized.authorId || DEFAULT_AUTHOR_ID,
    authorName: normalized.authorName || DEFAULT_AUTHOR.name,
    createdBy: normalized.createdBy || currentUser?.uid || '',
    createdAt: normalized.createdAt || now,
    publishedAt: normalized.status === 'published' ? (normalized.publishedAt || now) : normalized.publishedAt,
    slug: normalizeLocalizedSlug(normalized.slug),
    translationStatus: normalizeTranslationStatus(normalized.translationStatus),
    redirectTo: normalized.status === 'archived' && typeof normalized.redirectTo === 'string' && normalized.redirectTo !== normalized.id
      ? normalized.redirectTo
      : '',
  };
};

export const getCollectionRef = (collectionName) => collection(db, collectionName);

export const listCategories = async ({ includeInactive = false } = {}) => {
  if (!db) return [];

  const constraints = [orderBy('sortOrder', 'asc')];

  if (!includeInactive) {
    constraints.unshift(where('isActive', '==', true));
  }

  const snapshot = await getDocs(query(getCollectionRef(CATEGORIES_COLLECTION), ...constraints));
  return snapshot.docs.map(mapDocument);
};

export const getCategoryById = async (categoryId) => {
  if (!db || !categoryId) return null;

  const snapshot = await getDoc(doc(db, CATEGORIES_COLLECTION, categoryId));
  return snapshot.exists() ? mapDocument(snapshot) : null;
};

export const saveCategory = async (category, currentUser) => {
  if (!db) {
    throw new Error('Firestore is not configured.');
  }

  if (!currentUser?.uid) {
    throw new Error('Admin session required.');
  }

  const sanitized = sanitizeCategoryPayload(category);
  validateCategoryPayload(sanitized);

  const categoryRef = doc(db, CATEGORIES_COLLECTION, sanitized.id);

  try {
    const existingSnapshot = await getDoc(categoryRef);
    const payload = {
      ...sanitized,
      createdAt: existingSnapshot.exists() ? existingSnapshot.data().createdAt || sanitized.createdAt : sanitized.createdAt,
    };

    await setDoc(categoryRef, payload);
    return sanitized.id;
  } catch (error) {
    if (error?.code === 'permission-denied') {
      throw new Error('Firestore denied the category save write. Check the deployed categories rule and refresh the admin session token.');
    }
    throw error;
  }
};

export const deleteCategory = async (categoryId, currentUser) => {
  if (!db) {
    throw new Error('Firestore is not configured.');
  }

  if (!currentUser?.uid) {
    throw new Error('Admin session required.');
  }

  if (!categoryId) {
    throw new Error('Category id is required.');
  }

  try {
    await deleteDoc(doc(db, CATEGORIES_COLLECTION, categoryId));
  } catch (error) {
    if (error?.code === 'permission-denied') {
      throw new Error('Firestore denied the category delete write. Check the deployed categories rule and refresh the admin session token.');
    }
    throw error;
  }
};

// One author's published articles, newest first. Equality filters only, so
// no composite index; sorted here.
export const listPublishedArticlesByAuthor = async (authorId) => {
  if (!db) return [];
  const snapshot = await getDocs(query(
    getCollectionRef(ARTICLES_COLLECTION),
    where('status', '==', 'published'),
    where('authorId', '==', authorId),
  ));
  const millis = (value) => (typeof value?.toMillis === 'function' ? value.toMillis() : 0);
  return snapshot.docs.map(mapDocument).sort((a, b) => millis(b.publishedAt) - millis(a.publishedAt));
};

export const listPublishedArticles = async ({
  categoryId = null,
  cursor = null,
  pageSize = DEFAULT_ARTICLE_PAGE_SIZE,
} = {}) => {
  if (!db) {
    return { articles: [], nextCursor: null };
  }

  const constraints = [
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
    limit(pageSize),
  ];

  if (categoryId) {
    constraints.splice(1, 0, where('categoryIds', 'array-contains', categoryId));
  }

  if (cursor) {
    constraints.push(startAfter(cursor));
  }

  const snapshot = await getDocs(query(getCollectionRef(ARTICLES_COLLECTION), ...constraints));
  const articles = snapshot.docs.map(mapDocument);
  const nextCursor = snapshot.docs.length === pageSize ? snapshot.docs[snapshot.docs.length - 1] : null;

  return { articles, nextCursor };
};

// One aggregation query per category (cheap — counted server-side, no
// document reads) rather than a single grouped query, since Firestore has
// no "count grouped by" operator. Uses the same categoryIds+status index
// listPublishedArticles relies on.
export const getPublishedArticleCountsByCategory = async (categoryIds) => {
  if (!db || !Array.isArray(categoryIds) || categoryIds.length === 0) return {};

  const entries = await Promise.all(categoryIds.map(async (categoryId) => {
    const countSnapshot = await getCountFromServer(
      query(
        getCollectionRef(ARTICLES_COLLECTION),
        where('status', '==', 'published'),
        where('categoryIds', 'array-contains', categoryId)
      )
    );
    return [categoryId, countSnapshot.data().count];
  }));

  return Object.fromEntries(entries);
};

export const listAdminArticles = async ({ status = 'all' } = {}) => {
  if (!db) return [];

  const toMillis = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.seconds === 'number') return (value.seconds * 1000) + Math.floor((value.nanoseconds || 0) / 1000000);
    return 0;
  };

  const sortByRecency = (items) => (
    [...items].sort((a, b) => {
      const aTime = toMillis(a.updatedAt) || toMillis(a.createdAt);
      const bTime = toMillis(b.updatedAt) || toMillis(b.createdAt);
      return bTime - aTime;
    })
  );

  const loadPublishedFallback = async () => {
    const { articles: publishedArticles } = await listPublishedArticles({ pageSize: 100 });
    return sortByRecency(publishedArticles).map((article) => ({
      ...article,
      __publishedFallback: true,
    }));
  };

  try {
    const articleQuery = status === 'all'
      ? query(getCollectionRef(ARTICLES_COLLECTION))
      : query(getCollectionRef(ARTICLES_COLLECTION), where('status', '==', status));

    const snapshot = await getDocs(articleQuery);
    const articles = sortByRecency(mapSnapshotList(snapshot));

    if (articles.length > 0) {
      return articles;
    }

    if (status === 'all' || status === 'published') {
      const publishedFallback = await loadPublishedFallback();
      if (publishedFallback.length > 0) {
        return publishedFallback;
      }
    }

    const registrySnapshot = await getDocs(query(getCollectionRef(ARTICLE_SLUGS_COLLECTION), orderBy('updatedAt', 'desc')));
    const registryItems = sortByRecency(registrySnapshot.docs.map(mapRegistryArticle));

    if (status === 'all') {
      return registryItems;
    }

    return registryItems.filter((article) => article.status === status);
  } catch (error) {
    if (error?.code === 'permission-denied') {
      if (status !== 'all' && status !== 'published') {
        return [];
      }

      return loadPublishedFallback();
    }

    throw error;
  }
};

export const getArticleById = async (articleId) => {
  if (!db || !articleId) return null;

  const snapshot = await getDoc(doc(db, ARTICLES_COLLECTION, articleId));
  return snapshot.exists() ? mapDocument(snapshot) : null;
};

export const getPublishedArticleBySlug = async (locale, slug) => {
  if (!db) return null;

  const registryId = buildArticleSlugRegistryId(locale, slug);
  const registrySnapshot = await getDoc(doc(db, ARTICLE_SLUGS_COLLECTION, registryId));

  if (!registrySnapshot.exists()) {
    return null;
  }

  const { articleId, status } = registrySnapshot.data();
  if (status !== 'published') {
    return null;
  }

  const articleSnapshot = await getDoc(doc(db, ARTICLES_COLLECTION, articleId));

  if (!articleSnapshot.exists()) {
    return null;
  }

  const article = mapDocument(articleSnapshot);
  return article.status === 'published' ? article : null;
};

export const getArticleBySlug = async (locale, slug, { includeDrafts = false } = {}) => {
  if (!includeDrafts) {
    return getPublishedArticleBySlug(locale, slug);
  }

  if (!db) return null;

  const normalizedSlug = slugify(slug);
  const fieldPath = `slug.${locale}`;
  const snapshot = await getDocs(
    query(getCollectionRef(ARTICLES_COLLECTION), where(fieldPath, '==', normalizedSlug), limit(1))
  );

  if (snapshot.empty) {
    return null;
  }

  return mapDocument(snapshot.docs[0]);
};

export const saveArticle = async (article, currentUser) => {
  if (!db) {
    throw new Error('Firestore is not configured.');
  }

  if (!currentUser?.uid) {
    throw new Error('Admin session required.');
  }

  const articleId = article.id || doc(getCollectionRef(ARTICLES_COLLECTION)).id;
  const articleRef = doc(db, ARTICLES_COLLECTION, articleId);
  const payload = sanitizeArticlePayload({ ...article, id: articleId }, currentUser);
  const nextEntries = payload.status === 'draft' ? [] : buildSlugRegistryEntries(payload);
  validateArticlePayload(payload);

  if (!article.id) {
    try {
      await setDoc(articleRef, payload);

      if (nextEntries.length > 0) {
        const batch = writeBatch(db);
        nextEntries.forEach((entry) => {
          batch.set(doc(db, ARTICLE_SLUGS_COLLECTION, entry.registryId), {
            articleId,
            locale: entry.locale,
            slug: entry.slug,
            status: payload.status,
            updatedAt: Timestamp.now(),
          });
        });
        await batch.commit();
      }

      return articleId;
    } catch (error) {
      if (error?.code === 'permission-denied') {
        throw new Error('Firestore denied the article create write. If admin claim is valid, the most likely cause is the deployed articles rule or a stale auth connection in this tab.');
      }
      throw error;
    }
  }

  if (nextEntries.length === 0) {
    try {
      await setDoc(articleRef, payload);
      return articleId;
    } catch (error) {
      if (error?.code === 'permission-denied') {
        throw new Error('Firestore denied the article update write. Check the deployed articles rule and refresh the admin session token.');
      }
      throw error;
    }
  }

  try {
    await setDoc(articleRef, payload);

    const batch = writeBatch(db);
    nextEntries.forEach((entry) => {
      batch.set(doc(db, ARTICLE_SLUGS_COLLECTION, entry.registryId), {
        articleId,
        locale: entry.locale,
        slug: entry.slug,
        status: payload.status,
        updatedAt: Timestamp.now(),
      });
    });
    await batch.commit();
  } catch (error) {
    if (error?.code === 'permission-denied') {
      throw new Error('Firestore denied the article save write. If the token says admin=true, check whether the deployed rules are for the same project and whether the browser needs a fresh sign-in.');
    }
    throw error;
  }

  return articleId;
};

export const setArticleFeatured = async (articleId, featured, currentUser) => {
  if (!db) {
    throw new Error('Firestore is not configured.');
  }

  if (!currentUser?.uid) {
    throw new Error('Admin session required.');
  }

  if (!articleId || typeof articleId !== 'string') {
    throw new Error('Article id is required.');
  }

  try {
    await updateDoc(doc(db, ARTICLES_COLLECTION, articleId), {
      featured: Boolean(featured),
      updatedAt: Timestamp.now(),
      updatedBy: currentUser.uid,
    });
  } catch (error) {
    if (error?.code === 'permission-denied') {
      throw new Error('Firestore denied the featured-toggle write. Check the deployed articles rule and refresh the admin session token.');
    }
    throw error;
  }
};

export const deleteArticle = async (articleId, currentUser) => {
  if (!db) {
    throw new Error('Firestore is not configured.');
  }

  if (!currentUser?.uid) {
    throw new Error('Admin session required.');
  }

  if (!articleId || typeof articleId !== 'string') {
    throw new Error('Article id is required.');
  }

  const normalizedId = articleId.trim();
  if (!normalizedId) {
    throw new Error('Article id is required.');
  }

  try {
    const slugSnapshot = await getDocs(query(getCollectionRef(ARTICLE_SLUGS_COLLECTION), where('articleId', '==', normalizedId)));

    if (!slugSnapshot.empty) {
      const batch = writeBatch(db);
      slugSnapshot.docs.forEach((slugDoc) => {
        batch.delete(slugDoc.ref);
      });
      await batch.commit();
    }

    await deleteDoc(doc(db, ARTICLES_COLLECTION, normalizedId));
  } catch (error) {
    if (error?.code === 'permission-denied') {
      throw new Error('Firestore denied the article delete write. Check the deployed rules and refresh the admin session token.');
    }
    throw error;
  }
};

export const createPreviewUrl = (article, locale = 'en') => {
  const localizedSlug = article?.slug?.[locale] || article?.slug?.en || article?.slug?.es || '';
  if (!localizedSlug) return null;
  return `/${locale}/blog/${localizedSlug}?preview=1`;
};